import { useState } from "react";
import {
  Sparkles,
  Plus,
  Trash2,
  GripVertical,
  Image as ImageIcon,
  Type,
  AlignLeft,
  Grid2X2,
  Grid3X3,
  LayoutList,
  ChevronDown,
  RefreshCw,
  FolderOpen,
  Globe,
  Loader2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import type { TemplateConfig } from "../../../config/templateConfig";
import { axiosInstance } from "../../../lib/axios";
import PortfolioPickerModal, { type PortfolioAsset } from "./PortfolioPickerModal";
import InfographicSectionEditor from "./InfographicSectionEditor";

interface Props {
  section: any;
  templateConfig: TemplateConfig;
  onUpdate: (updates: any) => void;
  onRegenerateAI: (sectionId: string) => void;
}

const LAYOUT_OPTIONS: Record<string, { value: string; label: string; icon: any }[]> = {
  "simple-text": [
    { value: "card", label: "Card", icon: AlignLeft },
    { value: "full-width", label: "Full Width", icon: LayoutList },
    { value: "bordered", label: "Bordered", icon: Type },
  ],
  "feature-grid": [
    { value: "grid-2", label: "2 Columns", icon: Grid2X2 },
    { value: "grid-3", label: "3 Columns", icon: Grid3X3 },
    { value: "grid-4", label: "4 Columns", icon: Grid3X3 },
  ],
  "dark-grid": [
    { value: "grid-2", label: "2 Columns", icon: Grid2X2 },
    { value: "grid-3", label: "3 Columns", icon: Grid3X3 },
  ],
  "text-with-points": [
    { value: "split", label: "Split", icon: Grid2X2 },
    { value: "stacked", label: "Stacked", icon: LayoutList },
  ],
  "steps": [
    { value: "grid-3", label: "3 Columns", icon: Grid3X3 },
    { value: "vertical", label: "Vertical", icon: LayoutList },
    { value: "timeline", label: "Timeline", icon: LayoutList },
  ],
  "testimonial-grid": [
    { value: "grid-2", label: "2 Columns", icon: Grid2X2 },
    { value: "grid-3", label: "3 Columns", icon: Grid3X3 },
  ],
  "portfolio-grid": [
    { value: "grid-2", label: "2 Columns", icon: Grid2X2 },
    { value: "grid-3", label: "3 Columns", icon: Grid3X3 },
  ],
  "call-to-action": [
    { value: "centered", label: "Centered", icon: AlignLeft },
    { value: "split", label: "Split", icon: Grid2X2 },
  ],
};

// Sections that belong to the infographic template are edited by their own
// schema-driven editor; "portfolio" reuses the existing Portfolio editor so the
// Portfolio Library picker keeps working.
const INFOGRAPHIC_TYPES = [
  "cover", "client-info", "executive-summary", "understanding", "proposed-solution",
  "scope-of-work", "services", "deliverables", "approach", "process", "timeline",
  "pricing", "addons", "why-us", "portfolio", "testimonials", "terms", "next-steps", "contact",
];

const SectionEditor = ({
  section,
  templateConfig,
  onUpdate,
  onRegenerateAI,
}: Props) => {
  const [isRegenerating, setIsRegenerating] = useState(false);

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    await onRegenerateAI(section.id);
    setIsRegenerating(false);
  };

  const updateContent = (key: string, value: any) => {
    onUpdate({
      content: {
        ...section.content,
        [key]: value,
      },
    });
  };

  const layouts = LAYOUT_OPTIONS[section.type] || [];

  return (
    <div className="p-6 space-y-6">

      <div className="flex items-start justify-between">
        <div className="flex-1">
          <input
            type="text"
            value={section.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
            className="text-2xl font-bold text-gray-800 bg-transparent border-none outline-none w-full focus:ring-2 focus:ring-emerald-500 rounded px-2 -ml-2"
          />
          <p className="text-sm text-gray-500 mt-1">
            Section type: <span className="font-medium">{section.type}</span>
          </p>
        </div>

        <button
          onClick={handleRegenerate}
          disabled={isRegenerating}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors disabled:opacity-50"
        >
          {isRegenerating ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          Regenerate with AI
        </button>
      </div>

      {layouts.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Layout Style
          </label>
          <div className="flex gap-2">
            {layouts.map((layout) => {
              const Icon = layout.icon;
              return (
                <button
                  key={layout.value}
                  onClick={() => onUpdate({ layout: layout.value })}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all ${
                    section.layout === layout.value
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-gray-200 hover:border-gray-300 text-gray-600"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{layout.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-6">

   {section.type === "simple-text" && (
          <>
            <SimpleTextEditor content={section.content} onUpdate={updateContent} />

            {templateConfig.id === "modern" && section.id?.startsWith("overview") && (
              <ModernStoryEditor content={section.content} onUpdate={updateContent} />
            )}
          </>
        )}

 {(section.type === "feature-grid" || section.type === "dark-grid") && (
          <>
            <GridItemsEditor
              items={section.content?.items || []}
              onUpdate={(items) => updateContent("items", items)}
              itemFields={["title", "description"]}
              addLabel="Add Feature"
            />
            {templateConfig.id === "modern" && section.id?.startsWith("offer") && (
              <ModernToolsEditor
                tools={section.content?.tools || []}
                onUpdate={(tools) => updateContent("tools", tools)}
              />
            )}
          </>
        )}

        {section.type === "text-with-points" && (
          <TextWithPointsEditor
            content={section.content}
            onUpdate={updateContent}
          />
        )}

 {section.type === "steps" && (
          <>
            {templateConfig.id === "modern" ? (
              <ModernStepsEditor
                content={section.content}
                onUpdate={updateContent}
              />
            ) : (
              <StepsEditor
                steps={section.content?.steps || []}
                onUpdate={(steps) => updateContent("steps", steps)}
              />
            )}
          </>
        )}

        {section.type === "testimonial-grid" && (
          <TestimonialsEditor
            items={section.content?.items || []}
            onUpdate={(items) => updateContent("items", items)}
          />
        )}

        {section.type === "portfolio-grid" && (
          <PortfolioEditor
            content={section.content}
            onUpdate={updateContent}
          />
        )}

        {section.type === "website-showcase" && (
          <WebsiteShowcaseEditor
            items={section.content?.items || []}
            onUpdate={(items) => updateContent("items", items)}
          />
        )}

        {section.type === "call-to-action" && (
          <CTAEditor
            content={section.content}
            onUpdate={updateContent}
            primaryColor={templateConfig.colors.primary}
          />
        )}

        {section.type === "footer" && (
          <SimpleTextEditor
            content={section.content}
            onUpdate={updateContent}
          />
        )}

        {section.type === "stats" && (
          <StatsEditor
            items={section.content?.items || []}
            onUpdate={(items) => updateContent("items", items)}
          />
        )}

        {INFOGRAPHIC_TYPES.includes(section.type) && (
          <InfographicSectionEditor section={section} onUpdate={onUpdate} />
        )}
      </div>
    </div>
  );
};

// Website Showcase: paste a live URL → the server fetches its preview (title,
// description, image → CRE-hosted) and adds it as a card.
const WebsiteShowcaseEditor = ({
  items,
  onUpdate,
}: {
  items: any[];
  onUpdate: (items: any[]) => void;
}) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const addWebsite = async () => {
    const raw = url.trim();
    if (!raw) return;
    setBusy(true);
    try {
      const { data } = await axiosInstance.post("/proposals/website-preview", { url: raw });
      onUpdate([...items, data]);
      setUrl("");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't preview that website.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">Website URL</label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Globe size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addWebsite(); } }}
            placeholder="https://example.com"
            className="w-full rounded-xl border-2 border-gray-200 pl-9 pr-3 py-2.5 focus:border-emerald-500 outline-none text-sm"
          />
        </div>
        <button
          onClick={addWebsite}
          disabled={busy || !url.trim()}
          className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add
        </button>
      </div>

      <div className="mt-4 space-y-3">
        {items.length === 0 && <p className="text-sm text-gray-400">No websites added yet. Paste a URL above to create a showcase card.</p>}
        {items.map((w, i) => (
          <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-200 bg-gray-50">
            <div className="w-16 h-12 rounded-lg overflow-hidden bg-gray-200 shrink-0 grid place-items-center">
              {w.image ? <img src={w.image} alt="" className="w-full h-full object-cover" /> : <Globe size={18} className="text-gray-400" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-800 truncate">{w.title || w.url}</p>
              <p className="text-xs text-gray-500 truncate">{w.siteName || w.url}</p>
            </div>
            <button onClick={() => onUpdate(items.filter((_, j) => j !== i))} className="p-1.5 text-gray-400 hover:text-red-500 shrink-0"><X size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const SimpleTextEditor = ({
  content,
  onUpdate,
}: {
  content: any;
  onUpdate: (key: string, value: any) => void;
}) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-2">
      Content
    </label>
    <textarea
      value={content?.text || ""}
      onChange={(e) => onUpdate("text", e.target.value)}
      rows={6}
      className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 outline-none resize-none"
      placeholder="Enter your content here..."
    />
  </div>
);

const GridItemsEditor = ({
  items,
  onUpdate,
  itemFields,
  addLabel,
}: {
  items: any[];
  onUpdate: (items: any[]) => void;
  itemFields: string[];
  addLabel: string;
}) => {
  const addItem = () => {
    const newItem: any = {};
    itemFields.forEach((field) => {
      newItem[field] = "";
    });
    onUpdate([...items, newItem]);
  };

  const updateItem = (index: number, field: string, value: string) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    onUpdate(updated);
  };

  const removeItem = (index: number) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-semibold text-gray-700">
        Items ({items.length})
      </label>

      <div className="space-y-3">
        {items.map((item, index) => (
          <div
            key={index}
            className="bg-gray-50 rounded-xl p-4 border border-gray-200"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-200 rounded-lg cursor-grab">
                <GripVertical className="w-4 h-4 text-gray-400" />
              </div>

              <div className="flex-1 space-y-3">
                {itemFields.map((field) => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-gray-500 mb-1 capitalize">
                      {field}
                    </label>
                    {field === "description" ? (
                      <textarea
                        value={item[field] || ""}
                        onChange={(e) => updateItem(index, field, e.target.value)}
                        rows={2}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none resize-none"
                      />
                    ) : (
                      <input
                        type="text"
                        value={item[field] || ""}
                        onChange={(e) => updateItem(index, field, e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none"
                      />
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={() => removeItem(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={addItem}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-500 transition-colors w-full justify-center"
      >
        <Plus className="w-4 h-4" />
        {addLabel}
      </button>
    </div>
  );
};

const TextWithPointsEditor = ({
  content,
  onUpdate,
}: {
  content: any;
  onUpdate: (key: string, value: any) => void;
}) => {
  const points = content?.points || [];

  const addPoint = () => {
    onUpdate("points", [...points, ""]);
  };

  const updatePoint = (index: number, value: string) => {
    const updated = points.map((p: string, i: number) => (i === index ? value : p));
    onUpdate("points", updated);
  };

  const removePoint = (index: number) => {
    onUpdate("points", points.filter((_: any, i: number) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Main Text
        </label>
        <textarea
          value={content?.text || ""}
          onChange={(e) => onUpdate("text", e.target.value)}
          rows={4}
          className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-emerald-500 outline-none resize-none"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Key Points
        </label>
        <div className="space-y-2">
          {points.map((point: string, index: number) => (
            <div key={index} className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">
                {index + 1}
              </span>
              <input
                type="text"
                value={point}
                onChange={(e) => updatePoint(index, e.target.value)}
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none"
              />
              <button
                onClick={() => removePoint(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addPoint}
          className="mt-3 flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700"
        >
          <Plus className="w-4 h-4" />
          Add Point
        </button>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Highlight Box
        </label>
        <textarea
          value={content?.highlight || ""}
          onChange={(e) => onUpdate("highlight", e.target.value)}
          rows={2}
          className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-emerald-500 outline-none resize-none"
          placeholder="Key benefit or highlight text..."
        />
      </div>
    </div>
  );
};

const StepsEditor = ({
  steps,
  onUpdate,
}: {
  steps: any[];
  onUpdate: (steps: any[]) => void;
}) => {
  const addStep = () => {
    onUpdate([...steps, { title: "", description: "" }]);
  };

  const updateStep = (index: number, field: string, value: string) => {
    const updated = steps.map((step, i) =>
      i === index ? { ...step, [field]: value } : step
    );
    onUpdate(updated);
  };

  const removeStep = (index: number) => {
    onUpdate(steps.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-semibold text-gray-700">
        Process Steps ({steps.length})
      </label>

      <div className="space-y-3">
        {steps.map((step, index) => (
          <div
            key={index}
            className="bg-gray-50 rounded-xl p-4 border border-gray-200"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold shrink-0">
                {index + 1}
              </div>

              <div className="flex-1 space-y-3">
                <input
                  type="text"
                  value={step.title}
                  onChange={(e) => updateStep(index, "title", e.target.value)}
                  placeholder="Step title"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 font-medium focus:border-emerald-500 outline-none"
                />
                <textarea
                  value={step.description}
                  onChange={(e) => updateStep(index, "description", e.target.value)}
                  placeholder="Step description"
                  rows={2}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none resize-none"
                />
              </div>

              <button
                onClick={() => removeStep(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={addStep}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-500 transition-colors w-full justify-center"
      >
        <Plus className="w-4 h-4" />
        Add Step
      </button>
    </div>
  );
};

const TestimonialsEditor = ({
  items,
  onUpdate,
}: {
  items: any[];
  onUpdate: (items: any[]) => void;
}) => {
  const addTestimonial = () => {
    onUpdate([...items, { quote: "", name: "", position: "" }]);
  };

  const updateItem = (index: number, field: string, value: string) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    onUpdate(updated);
  };

  const removeItem = (index: number) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-semibold text-gray-700">
        Testimonials ({items.length})
      </label>

      <div className="space-y-4">
        {items.map((item, index) => (
          <div
            key={index}
            className="bg-gray-50 rounded-xl p-5 border border-gray-200"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Quote
                </label>
                <textarea
                  value={item.quote}
                  onChange={(e) => updateItem(index, "quote", e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none resize-none"
                  placeholder='"This was an amazing experience..."'
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => updateItem(index, "name", e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Position
                  </label>
                  <input
                    type="text"
                    value={item.position}
                    onChange={(e) => updateItem(index, "position", e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none"
                    placeholder="CEO, Company"
                  />
                </div>
              </div>

              <button
                onClick={() => removeItem(index)}
                className="text-sm text-red-500 hover:text-red-600"
              >
                Remove testimonial
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={addTestimonial}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-500 transition-colors w-full justify-center"
      >
        <Plus className="w-4 h-4" />
        Add Testimonial
      </button>
    </div>
  );
};

const PortfolioEditor = ({
  content,
  onUpdate,
}: {
  content: any;
  onUpdate: (key: string, value: any) => void;
}) => {
  const projects = content?.projects || [];
  const [showPicker, setShowPicker] = useState(false);

  const addProject = () => {
    onUpdate("projects", [...projects, { title: "", image: "" }]);
  };

  // Attach samples picked from File Storage (or freshly uploaded there).
  const addFromStorage = (assets: PortfolioAsset[]) => {
    onUpdate("projects", [
      ...projects,
      ...assets.map((a) => ({ title: a.name.replace(/\.[^/.]+$/, ""), image: a.url })),
    ]);
  };

  const updateProject = (index: number, field: string, value: string) => {
    const updated = projects.map((p: any, i: number) =>
      i === index ? { ...p, [field]: value } : p
    );
    onUpdate("projects", updated);
  };

  const removeProject = (index: number) => {
    onUpdate("projects", projects.filter((_: any, i: number) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Section Subtitle
        </label>
        <input
          type="text"
          value={content?.subtitle || ""}
          onChange={(e) => onUpdate("subtitle", e.target.value)}
          className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-emerald-500 outline-none"
          placeholder="e.g., Highlighted Projects"
        />
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-semibold text-gray-700">
          Projects ({projects.length})
        </label>

        <div className="grid grid-cols-2 gap-4">
          {projects.map((project: any, index: number) => (
            <div
              key={index}
              className="relative bg-gray-50 rounded-xl overflow-hidden border border-gray-200 group"
            >
              {project.image ? (
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-32 object-cover"
                />
              ) : (
                <div className="w-full h-32 bg-gray-200 flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                </div>
              )}

              <div className="p-3 space-y-2">
                <input
                  type="text"
                  value={project.title}
                  onChange={(e) => updateProject(index, "title", e.target.value)}
                  placeholder="Project title"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none"
                />
                <input
                  type="text"
                  value={project.image}
                  onChange={(e) => updateProject(index, "image", e.target.value)}
                  placeholder="Image URL"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs focus:border-emerald-500 outline-none"
                />
              </div>

              <button
                onClick={() => removeProject(index)}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setShowPicker(true)}
            className="flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-emerald-300 text-emerald-600 hover:border-emerald-500 hover:bg-emerald-50 transition-colors justify-center"
          >
            <FolderOpen className="w-4 h-4" />
            Add from File Storage / Upload
          </button>
          <button
            onClick={addProject}
            className="flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-500 transition-colors justify-center"
          >
            <Plus className="w-4 h-4" />
            Add Manually
          </button>
        </div>
      </div>

      {showPicker && (
        <PortfolioPickerModal onClose={() => setShowPicker(false)} onPick={addFromStorage} />
      )}
    </div>
  );
};

const CTAEditor = ({
  content,
  onUpdate,
  primaryColor,
}: {
  content: any;
  onUpdate: (key: string, value: any) => void;
  primaryColor: string;
}) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        CTA Text
      </label>
      <textarea
        value={content?.text || ""}
        onChange={(e) => onUpdate("text", e.target.value)}
        rows={3}
        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-emerald-500 outline-none resize-none"
        placeholder="Enter your call-to-action message..."
      />
    </div>

    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        Button Text
      </label>
      <div className="flex items-center gap-4">
        <input
          type="text"
          value={content?.buttonText || ""}
          onChange={(e) => onUpdate("buttonText", e.target.value)}
          className="flex-1 rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-emerald-500 outline-none"
          placeholder="e.g., Hire Me Now"
        />
        <div
          className="px-6 py-3 rounded-xl text-white font-semibold"
          style={{ backgroundColor: primaryColor }}
        >
          {content?.buttonText || "Button Preview"}
        </div>
      </div>
    </div>

    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        Button Link <span className="text-gray-400 font-normal">(Upwork / profile — where the button goes)</span>
      </label>
      <input
        type="text"
        value={content?.buttonLink || ""}
        onChange={(e) => onUpdate("buttonLink", e.target.value)}
        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-emerald-500 outline-none"
        placeholder="https://www.upwork.com/freelancers/..."
      />
    </div>
  </div>
);

const StatsEditor = ({
  items,
  onUpdate,
}: {
  items: any[];
  onUpdate: (items: any[]) => void;
}) => {
  const addStat = () => {
    onUpdate([...items, { value: "", label: "" }]);
  };

  const updateItem = (index: number, field: string, value: string) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    onUpdate(updated);
  };

  const removeItem = (index: number) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-semibold text-gray-700">
        Statistics ({items.length})
      </label>

      <div className="grid grid-cols-2 gap-4">
        {items.map((item, index) => (
          <div
            key={index}
            className="bg-gray-50 rounded-xl p-4 border border-gray-200"
          >
            <div className="space-y-3">
              <input
                type="text"
                value={item.value}
                onChange={(e) => updateItem(index, "value", e.target.value)}
                placeholder="100%"
                className="w-full text-2xl font-bold text-center rounded-lg border border-gray-200 px-3 py-2 focus:border-emerald-500 outline-none"
              />
              <input
                type="text"
                value={item.label}
                onChange={(e) => updateItem(index, "label", e.target.value)}
                placeholder="Job Success"
                className="w-full text-center rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none"
              />
              <button
                onClick={() => removeItem(index)}
                className="w-full text-xs text-red-500 hover:text-red-600"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={addStat}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-500 transition-colors w-full justify-center"
      >
        <Plus className="w-4 h-4" />
        Add Statistic
      </button>
    </div>
  );
};

export default SectionEditor;

const ModernStoryEditor = ({
  content,
  onUpdate,
}: {
  content: any;
  onUpdate: (key: string, value: any) => void;
}) => {
  const timeline = content?.timeline || [];
  const credChips = content?.credChips || [];
  const storyBody = content?.storyBody || [];

  return (
    <div className="space-y-6 mt-6 pt-6 border-t-2 border-amber-200">
      <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
        <h4 className="font-bold text-amber-800 text-sm mb-1">
          ✨ Modern Template — Story Section
        </h4>
        <p className="text-xs text-amber-700">
          These fields power the split story layout unique to the Modern template.
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Story Headline (use \n for line breaks)
        </label>
        <textarea
          value={content?.storyHeadline || ""}
          onChange={(e) => onUpdate("storyHeadline", e.target.value)}
          rows={4}
          className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-amber-500 outline-none resize-none font-mono text-sm"
          placeholder={"I didn't start\nas a designer.\nI started as a\nproblem solver."}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Pullquote
          </label>
          <textarea
            value={content?.pullquote || ""}
            onChange={(e) => onUpdate("pullquote", e.target.value)}
            rows={3}
            className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-amber-500 outline-none resize-none"
            placeholder="Your first impression isn't your handshake..."
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Attribution
          </label>
          <input
            type="text"
            value={content?.pullquoteAttr || ""}
            onChange={(e) => onUpdate("pullquoteAttr", e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-500 outline-none"
            placeholder="— Irfan Baig"
          />
          <label className="block text-sm font-semibold text-gray-700 mb-2 mt-4">
            Right Pill Label
          </label>
          <input
            type="text"
            value={content?.storyRightPill || ""}
            onChange={(e) => onUpdate("storyRightPill", e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-500 outline-none"
            placeholder="Philosophy"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Story Body Paragraphs ({storyBody.length})
        </label>
        <div className="space-y-2">
          {storyBody.map((para: string, i: number) => (
            <div key={i} className="flex gap-2">
              <textarea
                value={para}
                onChange={(e) => {
                  const updated = [...storyBody];
                  updated[i] = e.target.value;
                  onUpdate("storyBody", updated);
                }}
                rows={3}
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-500 outline-none resize-none"
              />
              <button
                onClick={() => onUpdate("storyBody", storyBody.filter((_: any, idx: number) => idx !== i))}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg self-start"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => onUpdate("storyBody", [...storyBody, ""])}
          className="mt-2 text-sm text-amber-600 hover:text-amber-700 font-medium"
        >
          + Add Paragraph
        </button>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Timeline Entries ({timeline.length})
        </label>
        <div className="space-y-3">
          {timeline.map((entry: any, i: number) => (
            <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Icon</label>
                  <input
                    type="text"
                    value={entry.icon || ""}
                    onChange={(e) => {
                      const updated = [...timeline];
                      updated[i] = { ...updated[i], icon: e.target.value };
                      onUpdate("timeline", updated);
                    }}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-center text-lg focus:border-amber-500 outline-none"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-xs text-gray-500 mb-1">Year/Phase Label</label>
                  <input
                    type="text"
                    value={entry.year || ""}
                    onChange={(e) => {
                      const updated = [...timeline];
                      updated[i] = { ...updated[i], year: e.target.value };
                      onUpdate("timeline", updated);
                    }}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-500 outline-none"
                    placeholder="e.g., The Realization"
                  />
                </div>
              </div>
              <div className="mt-2">
                <input
                  type="text"
                  value={entry.title || ""}
                  onChange={(e) => {
                    const updated = [...timeline];
                    updated[i] = { ...updated[i], title: e.target.value };
                    onUpdate("timeline", updated);
                  }}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium focus:border-amber-500 outline-none"
                  placeholder="Title"
                />
              </div>
              <div className="mt-2 flex gap-2">
                <textarea
                  value={entry.desc || ""}
                  onChange={(e) => {
                    const updated = [...timeline];
                    updated[i] = { ...updated[i], desc: e.target.value };
                    onUpdate("timeline", updated);
                  }}
                  rows={2}
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-500 outline-none resize-none"
                  placeholder="Description"
                />
                <button
                  onClick={() => onUpdate("timeline", timeline.filter((_: any, idx: number) => idx !== i))}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg self-start"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={() => onUpdate("timeline", [...timeline, { icon: "💡", year: "", title: "", desc: "" }])}
          className="mt-2 text-sm text-amber-600 hover:text-amber-700 font-medium"
        >
          + Add Timeline Entry
        </button>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Credential Chips ({credChips.length})
        </label>
        <div className="flex gap-2 flex-wrap">
          {credChips.map((chip: any, i: number) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
              <input
                type="text"
                value={chip.icon || ""}
                onChange={(e) => {
                  const updated = [...credChips];
                  updated[i] = { ...updated[i], icon: e.target.value };
                  onUpdate("credChips", updated);
                }}
                className="w-10 text-center border border-gray-200 rounded px-1 py-0.5 text-lg focus:border-amber-500 outline-none"
              />
              <input
                type="text"
                value={chip.text || ""}
                onChange={(e) => {
                  const updated = [...credChips];
                  updated[i] = { ...updated[i], text: e.target.value };
                  onUpdate("credChips", updated);
                }}
                className="w-32 border border-gray-200 rounded px-2 py-0.5 text-xs focus:border-amber-500 outline-none"
              />
              <button
                onClick={() => onUpdate("credChips", credChips.filter((_: any, idx: number) => idx !== i))}
                className="text-red-400 hover:text-red-600 text-sm"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => onUpdate("credChips", [...credChips, { icon: "⭐", text: "" }])}
          className="mt-2 text-sm text-amber-600 hover:text-amber-700 font-medium"
        >
          + Add Chip
        </button>
      </div>
    </div>
  );
};

const ModernStepsEditor = ({
  content,
  onUpdate,
}: {
  content: any;
  onUpdate: (key: string, value: any) => void;
}) => {
  const steps = content?.steps || [];

  return (
    <div className="space-y-4">

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Process Subtitle
        </label>
        <input
          type="text"
          value={content?.subtitle || ""}
          onChange={(e) => onUpdate("subtitle", e.target.value)}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none"
          placeholder="A structured approach that removes guesswork..."
        />
      </div>

      <label className="block text-sm font-semibold text-gray-700">
        Process Steps ({steps.length})
      </label>

      <div className="space-y-3">
        {steps.map((step: any, index: number) => (
          <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <div className="flex items-start gap-3">

              <div>
                <label className="block text-xs text-gray-500 mb-1">Icon</label>
                <input
                  type="text"
                  value={step.icon || ""}
                  onChange={(e) => {
                    const updated = steps.map((s: any, i: number) =>
                      i === index ? { ...s, icon: e.target.value } : s
                    );
                    onUpdate("steps", updated);
                  }}
                  className="w-14 text-center text-lg rounded-lg border border-gray-200 px-2 py-2 focus:border-emerald-500 outline-none"
                />
              </div>

              <div className="flex-1 space-y-3">
                <input
                  type="text"
                  value={step.title}
                  onChange={(e) => {
                    const updated = steps.map((s: any, i: number) =>
                      i === index ? { ...s, title: e.target.value } : s
                    );
                    onUpdate("steps", updated);
                  }}
                  placeholder="Step title"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 font-medium focus:border-emerald-500 outline-none"
                />
                <textarea
                  value={step.description}
                  onChange={(e) => {
                    const updated = steps.map((s: any, i: number) =>
                      i === index ? { ...s, description: e.target.value } : s
                    );
                    onUpdate("steps", updated);
                  }}
                  placeholder="Step description"
                  rows={2}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none resize-none"
                />
              </div>

              <button
                onClick={() => onUpdate("steps", steps.filter((_: any, i: number) => i !== index))}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={() => onUpdate("steps", [...steps, { title: "", description: "", icon: "⚡" }])}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-500 transition-colors w-full justify-center"
      >
        + Add Step
      </button>
    </div>
  );
};

const ModernToolsEditor = ({
  tools,
  onUpdate,
}: {
  tools: any[];
  onUpdate: (tools: any[]) => void;
}) => (
  <div className="mt-6 pt-6 border-t border-gray-200">
    <label className="block text-sm font-semibold text-gray-700 mb-2">
      🔧 Tools & Technologies ({tools.length})
    </label>
    <div className="flex gap-2 flex-wrap">
      {tools.map((tool: any, i: number) => (
        <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
          <input
            type="text"
            value={tool.icon || ""}
            onChange={(e) => {
              const updated = [...tools];
              updated[i] = { ...updated[i], icon: e.target.value };
              onUpdate(updated);
            }}
            className="w-10 text-center text-lg border border-gray-200 rounded px-1 py-0.5 focus:border-emerald-500 outline-none"
          />
          <input
            type="text"
            value={tool.name || ""}
            onChange={(e) => {
              const updated = [...tools];
              updated[i] = { ...updated[i], name: e.target.value };
              onUpdate(updated);
            }}
            className="w-32 border border-gray-200 rounded px-2 py-0.5 text-sm focus:border-emerald-500 outline-none"
          />
          <button
            onClick={() => onUpdate(tools.filter((_: any, idx: number) => idx !== i))}
            className="text-red-400 hover:text-red-600 text-sm"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
    <button
      onClick={() => onUpdate([...tools, { icon: "⚡", name: "" }])}
      className="mt-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
    >
      + Add Tool
    </button>
  </div>
);
